import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Search, Receipt, Upload, Sparkles, Settings, Check, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { formatCurrency } from '../lib/utils';
import UploadReceiptModal from '../components/expenses/UploadReceiptModal';
import AddManualExpenseModal from '../components/expenses/AddManualExpenseModal';
import ExpenseDetailsModal from '../components/expenses/ExpenseDetailsModal';
import ManageCategoriesModal from '../components/expenses/ManageCategoriesModal';
import ExpenseReceiptPreview from '../components/expenses/ExpenseReceiptPreview';
import { getExpenseReceiptInfo } from '../lib/expenseReceiptDisplay';
import {
  approveExpense,
  expenseNeedsApproval,
  expenseReceiptFailed,
  expenseReceiptIsProcessing,
} from '../lib/expenseApproval';
import { reprocessExpenseReceipt } from '../lib/expenseReceiptBackgroundProcessor';

interface ExpenseCategory {
  id: string;
  name: string;
  color?: string;
  icon?: string;
}

interface Expense {
  id: string;
  business_id: string;
  category_id?: string;
  amount: number;
  currency: string;
  description: string;
  merchant_name?: string;
  expense_date: string;
  payment_method?: string;
  tax_amount?: number;
  tax_rate?: number;
  tax_breakdown?: { name: string; rate: number | null; amount: number }[];
  is_tax_deductible: boolean;
  receipt_image_url?: string;
  ai_categorized: boolean;
  ai_category_suggestion?: string;
  ai_confidence_score?: number;
  approval_status?: 'pending' | 'approved';
  approved_at?: string | null;
  notes?: string;
  created_at: string;
  updated_at: string;
  category?: ExpenseCategory;
  receipts?: any[];
  line_items?: any[];
}

/**
 * Expenses - Expenses Addon App
 * 
 * AI-powered expense tracking with automatic receipt categorization.
 * This addon app allows businesses to track expenses, upload receipts,
 * and automatically categorize them using AI.
 */
export default function Expenses() {
  const { selectedBusiness } = useBusiness();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<'this-month' | 'last-month' | 'this-year' | 'all-time'>('this-month');
  const [isUploadReceiptModalOpen, setIsUploadReceiptModalOpen] = useState(false);
  const [isManualExpenseModalOpen, setIsManualExpenseModalOpen] = useState(false);
  const [manualExpensePreselection, setManualExpensePreselection] = useState<{
    bookingId?: string | null;
    projectId?: string | null;
  } | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [reprocessingId, setReprocessingId] = useState<string | null>(null);
  /** Pending approvals for the whole business (not limited by date filter). */
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  // Date filtering function
  const getDateRange = (filter: string) => {
    const now = new Date();
    const createDateAtMidnight = (year: number, month: number, day: number) => {
      return new Date(year, month, day, 0, 0, 0, 0);
    };
    const createDateAtEndOfDay = (year: number, month: number, day: number) => {
      return new Date(year, month, day, 23, 59, 59, 999);
    };

    switch (filter) {
      case 'this-month':
        return {
          start: createDateAtMidnight(now.getFullYear(), now.getMonth(), 1),
          end: createDateAtEndOfDay(now.getFullYear(), now.getMonth(), now.getDate())
        };
      case 'last-month':
        const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
        const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        const lastDayOfLastMonth = new Date(lastMonthYear, lastMonth + 1, 0).getDate();
        return {
          start: createDateAtMidnight(lastMonthYear, lastMonth, 1),
          end: createDateAtEndOfDay(lastMonthYear, lastMonth, lastDayOfLastMonth)
        };
      case 'this-year':
        return {
          start: createDateAtMidnight(now.getFullYear(), 0, 1),
          end: createDateAtEndOfDay(now.getFullYear(), now.getMonth(), now.getDate())
        };
      case 'all-time':
      default:
        return {
          start: new Date(0),
          end: createDateAtEndOfDay(now.getFullYear(), now.getMonth(), now.getDate())
        };
    }
  };

  const fetchPendingApprovalCount = async () => {
    if (!selectedBusiness?.id) return;

    const { count, error } = await supabase
      .from('expenses')
      .select('id', { count: 'exact', head: true })
      .eq('business_id', selectedBusiness.id)
      .eq('approval_status', 'pending');

    if (!error) {
      setPendingApprovalCount(count ?? 0);
    }
  };

  // Fetch expenses
  const fetchExpenses = async () => {
    if (!selectedBusiness?.id) return;

    setIsLoading(true);
    try {
      let query = supabase
        .from('expenses')
        .select(`
          *,
          category:expense_categories(*),
          receipts:expense_receipts(*),
          line_items:expense_line_items(*)
        `)
        .eq('business_id', selectedBusiness.id);

      if (dateFilter !== 'all-time') {
        const { start, end } = getDateRange(dateFilter);
        const startStr = start.toISOString().split('T')[0];
        const endStr = end.toISOString().split('T')[0];
        query = query
          .gte('expense_date', startStr)
          .lte('expense_date', endStr);
      }

      const { data, error } = await query.order('expense_date', { ascending: false });

      if (error) {
        console.error('Error fetching expenses:', error);
        toast.error('Failed to load expenses');
        return;
      }

      setExpenses(data || []);
    } catch (error) {
      console.error('Error in fetchExpenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const { data, error } = await supabase
        .from('expense_categories')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('is_active', true)
        .order('order_index');

      if (error) {
        console.error('Error fetching categories:', error);
        return;
      }

      setCategories(data || []);

      // If no categories exist, create default ones
      if (!data || data.length === 0) {
        const { error: createError } = await supabase.rpc('create_default_expense_categories', {
          p_business_id: selectedBusiness.id
        });

        if (!createError) {
          // Refetch categories
          const { data: newCategories } = await supabase
            .from('expense_categories')
            .select('*')
            .eq('business_id', selectedBusiness.id)
            .eq('is_active', true)
            .order('order_index');

          setCategories(newCategories || []);
        }
      }
    } catch (error) {
      console.error('Error in fetchCategories:', error);
    }
  };

  const refreshExpensesData = () => {
    void fetchExpenses();
    void fetchPendingApprovalCount();
  };

  useEffect(() => {
    if (selectedBusiness?.id) {
      fetchCategories();
      refreshExpensesData();
    }
  }, [selectedBusiness?.id, dateFilter]);

  useEffect(() => {
    const onExpensesUpdated = () => {
      if (selectedBusiness?.id) refreshExpensesData();
    };
    window.addEventListener('expensesUpdated', onExpensesUpdated);
    return () => window.removeEventListener('expensesUpdated', onExpensesUpdated);
  }, [selectedBusiness?.id, dateFilter]);

  useEffect(() => {
    if (!selectedExpense) return;
    const fresh = expenses.find((expense) => expense.id === selectedExpense.id);
    if (!fresh || fresh === selectedExpense) return;
    const prevStatus = selectedExpense.receipts?.[0]?.processing_status;
    const nextStatus = fresh.receipts?.[0]?.processing_status;
    if (prevStatus !== nextStatus || fresh.updated_at !== selectedExpense.updated_at) {
      setSelectedExpense(fresh);
    }
  }, [expenses, selectedExpense]);

  // Filter expenses (single list — needs-approval is a row state, not a separate filter)
  const filteredExpenses = useMemo(() => {
    let filtered = expenses;

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(expense => expense.category_id === selectedCategory);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(expense =>
        expense.description.toLowerCase().includes(query) ||
        expense.merchant_name?.toLowerCase().includes(query) ||
        expense.category?.name.toLowerCase().includes(query)
      );
    }

    // Pending approvals first, then by date (already roughly date-sorted from query)
    return [...filtered].sort((a, b) => {
      const aPending = expenseNeedsApproval(a) ? 0 : 1;
      const bPending = expenseNeedsApproval(b) ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;
      return new Date(b.expense_date).getTime() - new Date(a.expense_date).getTime();
    });
  }, [expenses, selectedCategory, searchQuery]);

  const totals = useMemo(() => {
    const subtotal = filteredExpenses.reduce((sum, expense) => sum + parseFloat(expense.amount.toString()), 0);
    const taxTotal = filteredExpenses.reduce((sum, expense) => sum + (parseFloat(expense.tax_amount?.toString() || '0')), 0);
    return { subtotal, taxTotal, grandTotal: subtotal + taxTotal };
  }, [filteredExpenses]);

  const handleReprocessReceipt = async (expenseId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!selectedBusiness?.id || reprocessingId) return;
    setReprocessingId(expenseId);
    try {
      await reprocessExpenseReceipt({
        expenseId,
        businessId: selectedBusiness.id,
        categories,
      });
    } catch (err) {
      console.error('Error reprocessing receipt:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to reprocess receipt');
    } finally {
      setReprocessingId(null);
    }
  };

  const handleApproveExpense = async (expenseId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    setApprovingId(expenseId);
    try {
      await approveExpense(expenseId);
      toast.success('Marked as reviewed');
      refreshExpensesData();
    } catch (err) {
      console.error('Error approving expense:', err);
      toast.error('Failed to approve expense');
    } finally {
      setApprovingId(null);
    }
  };

  // Handle delete expense
  const handleDeleteExpense = async (expenseId: string) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;

    try {
      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', expenseId);

      if (error) throw error;

      toast.success('Expense deleted successfully');
      setIsDetailsModalOpen(false);
      setSelectedExpense(null);
      refreshExpensesData();
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  // Handle expense created/updated
  const handleExpenseSaved = () => {
    refreshExpensesData();
    setIsUploadReceiptModalOpen(false);
    setIsManualExpenseModalOpen(false);
  };

  const handleExpenseUpdated = () => {
    refreshExpensesData();
    setIsDetailsModalOpen(false);
    setSelectedExpense(null);
  };

  return (
    <div
      className="flex flex-col h-full overflow-y-auto bg-gray-50 min-h-screen"
      data-expenses-page
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        // No paddingTop — App shell already offsets content under the nav + safe area.
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div
        className="pb-8"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif' }}
      >
        {/* Toolbar — mobile: standard blue + opens upload; desktop: categories + upload */}
        <div className="px-4 pt-3 sm:pt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setIsManageCategoriesOpen(true)}
            className="hidden sm:inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-[10px] bg-gray-100 text-gray-700 text-[13px] font-semibold hover:bg-gray-200 transition-colors"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            title="Manage expense categories"
          >
            <Settings className="h-4 w-4" />
            Categories
          </button>
          <button
            type="button"
            onClick={() => setIsUploadReceiptModalOpen(true)}
            className="hidden sm:inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-[10px] bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 shadow-sm transition-colors"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Upload className="h-4 w-4" />
            Upload Receipt
          </button>
          <button
            type="button"
            onClick={() => setIsUploadReceiptModalOpen(true)}
            className="sm:hidden flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700 active:scale-[0.98] touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label="Add expense"
            title="Add expense"
          >
            <Plus className="h-5 w-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 px-4 pt-3 pb-2">
          <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
            <p className="text-[11px] font-medium text-gray-600 truncate">Subtotal</p>
            <p className="text-[16px] font-bold text-gray-900 truncate">{formatCurrency(totals.subtotal)}</p>
            <p className="text-[11px] text-gray-400 mt-0.5 truncate">
              {filteredExpenses.length} expense{filteredExpenses.length === 1 ? '' : 's'}
              {filteredExpenses.some((e) => expenseNeedsApproval(e))
                ? ` · ${filteredExpenses.filter((e) => expenseNeedsApproval(e)).length} to review`
                : ''}
            </p>
          </div>
          <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
            <p className="text-[11px] font-medium text-gray-600 truncate">Tax</p>
            <p className="text-[16px] font-bold text-gray-900 truncate">{formatCurrency(totals.taxTotal)}</p>
          </div>
          <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0 col-span-2 sm:col-span-1">
            <p className="text-[11px] font-medium text-gray-600 truncate">Grand Total</p>
            <p className="text-[16px] font-bold text-red-600 truncate">{formatCurrency(totals.grandTotal)}</p>
          </div>
        </div>

        {pendingApprovalCount > 0 ? (
          <div className="px-4 pt-1 pb-1">
            <div className="flex w-full items-start gap-3 rounded-[12px] border border-amber-200/60 bg-amber-50 px-4 py-3">
              <Sparkles className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-amber-900">
                  {pendingApprovalCount} receipt{pendingApprovalCount !== 1 ? 's' : ''} categorized by AI
                </p>
                <p className="mt-0.5 text-[12px] text-amber-800">
                  They already count as expenses. Approve one when you have double-checked it.
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Filters */}
        <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search description, merchant, category"
              className="w-full pl-9 pr-3 py-2.5 bg-gray-100 border-0 rounded-[10px] text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
            className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="this-month">This Month</option>
            <option value="last-month">Last Month</option>
            <option value="this-year">This Year</option>
            <option value="all-time">All Time</option>
          </select>
        </div>

        {/* List */}
        <div className="px-4">
          {isLoading ? (
            <div className="py-16 text-center text-gray-500 text-sm">Loading expenses…</div>
          ) : filteredExpenses.length === 0 ? (
            <div className="py-16 text-center">
              <Receipt className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm mb-3">
                {searchQuery || selectedCategory !== 'all'
                  ? 'No expenses match your filters.'
                  : 'No expenses for this period.'}
              </p>
              <button
                type="button"
                onClick={() => setIsUploadReceiptModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[10px] bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 shadow-sm"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Upload className="h-4 w-4" />
                Upload Receipt
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden divide-y divide-gray-100">
              {filteredExpenses.map((expense) => {
                const isProcessingReceipt = expenseReceiptIsProcessing(expense);
                const receiptFailed = expenseReceiptFailed(expense);
                const needsApproval = expenseNeedsApproval(expense);
                const isApproving = approvingId === expense.id;
                const isReprocessing = reprocessingId === expense.id;
                const total = expense.amount + (expense.tax_amount || 0);

                return (
                  <div
                    key={expense.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedExpense(expense);
                      setIsDetailsModalOpen(true);
                    }}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        setSelectedExpense(expense);
                        setIsDetailsModalOpen(true);
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer ${
                      receiptFailed ? 'bg-red-50/50' : needsApproval ? 'bg-amber-50/40' : ''
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {expense.receipt_image_url ? (
                      <div className="relative flex-shrink-0">
                        <ExpenseReceiptPreview
                          {...getExpenseReceiptInfo(expense)}
                          url={expense.receipt_image_url}
                          variant="thumbnail"
                          className="h-10 w-10"
                          imgClassName="h-10 w-10 rounded-[8px]"
                        />
                        {expense.ai_categorized && !isProcessingReceipt ? (
                          <div className="absolute -top-1 -right-1 bg-blue-500 rounded-full p-0.5">
                            <Sparkles className="h-2.5 w-2.5 text-white" />
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="h-10 w-10 bg-gray-100 rounded-[8px] border border-gray-200/60 flex items-center justify-center flex-shrink-0">
                        <Receipt className="h-5 w-5 text-gray-400" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[15px] font-semibold text-gray-900 truncate">
                          {expense.description}
                        </span>
                        {isProcessingReceipt ? (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-100 text-amber-800 flex-shrink-0 animate-pulse">
                            Processing…
                          </span>
                        ) : receiptFailed ? (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-700 flex-shrink-0">
                            Failed
                          </span>
                        ) : expense.ai_categorized ? (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 flex-shrink-0">
                            Categorized by AI
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 min-w-0 text-[12px] text-gray-500">
                        {expense.merchant_name ? (
                          <span className="truncate">{expense.merchant_name}</span>
                        ) : null}
                        {expense.merchant_name ? <span className="text-gray-300">·</span> : null}
                        <span className="flex-shrink-0">
                          {new Date(expense.expense_date).toLocaleDateString()}
                        </span>
                        {expense.category ? (
                          <>
                            <span className="text-gray-300">·</span>
                            <span
                              className="inline-block px-1.5 py-0.5 rounded text-[11px] font-medium flex-shrink-0 truncate max-w-[8rem]"
                              style={{
                                backgroundColor: expense.category.color
                                  ? `${expense.category.color}20`
                                  : '#F3F4F6',
                                color: expense.category.color || '#6B7280',
                              }}
                            >
                              {expense.category.name}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>

                    <div
                      className="text-right flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="text-[15px] font-bold text-gray-900">
                        {formatCurrency(total)}
                      </div>
                      {expense.tax_amount && expense.tax_amount > 0 ? (
                        <div className="text-[11px] text-gray-500">
                          {expense.tax_breakdown && expense.tax_breakdown.length > 0
                            ? expense.tax_breakdown
                                .map((t) => `${t.name}: ${formatCurrency(t.amount)}`)
                                .join(' + ')
                            : `Tax: ${formatCurrency(expense.tax_amount)}`}
                        </div>
                      ) : null}
                    </div>

                    {receiptFailed ? (
                      <button
                        type="button"
                        onClick={(e) => void handleReprocessReceipt(expense.id, e)}
                        disabled={isReprocessing}
                        className="h-8 px-2.5 inline-flex items-center justify-center gap-1 rounded-[10px] bg-gray-900 text-white text-[12px] font-semibold hover:bg-gray-800 disabled:opacity-60 flex-shrink-0 transition-colors"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        aria-label="Reprocess receipt"
                        title="Run AI on this receipt again"
                      >
                        {isReprocessing ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        <span className="hidden sm:inline">Reprocess</span>
                      </button>
                    ) : needsApproval ? (
                      <button
                        type="button"
                        onClick={(e) => void handleApproveExpense(expense.id, e)}
                        disabled={isApproving}
                        className="h-8 px-2.5 inline-flex items-center justify-center gap-1 rounded-[10px] bg-emerald-600 text-white text-[12px] font-semibold hover:bg-emerald-700 disabled:opacity-60 flex-shrink-0 transition-colors"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        aria-label="Approve categorization"
                        title="Approve categorization"
                      >
                        {isApproving ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                        )}
                        <span className="hidden sm:inline">Approve</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setSelectedExpense(expense);
                          setIsDetailsModalOpen(true);
                        }}
                        className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0 transition-colors"
                        title="Open expense"
                        aria-label="Open expense"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <UploadReceiptModal
        isOpen={isUploadReceiptModalOpen}
        onClose={() => setIsUploadReceiptModalOpen(false)}
        onSave={handleExpenseSaved}
        businessId={selectedBusiness?.id || ''}
        categories={categories}
        onCategoriesChange={fetchCategories}
        onSwitchToManual={(preselection) => {
          setIsUploadReceiptModalOpen(false);
          setManualExpensePreselection(preselection ?? null);
          setIsManualExpenseModalOpen(true);
        }}
      />

      <AddManualExpenseModal
        isOpen={isManualExpenseModalOpen}
        onClose={() => {
          setIsManualExpenseModalOpen(false);
          setManualExpensePreselection(null);
        }}
        onSave={handleExpenseSaved}
        businessId={selectedBusiness?.id || ''}
        categories={categories}
        onCategoriesChange={fetchCategories}
        initialBookingId={manualExpensePreselection?.bookingId || undefined}
        initialProjectId={manualExpensePreselection?.projectId || undefined}
      />

      <ExpenseDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedExpense(null);
        }}
        expense={selectedExpense}
        onUpdate={() => {
          handleExpenseUpdated();
          setApprovingId(null);
        }}
        onDelete={handleDeleteExpense}
        categories={categories}
        onCategoriesChange={fetchCategories}
      />

      <ManageCategoriesModal
        isOpen={isManageCategoriesOpen}
        onClose={() => setIsManageCategoriesOpen(false)}
        businessId={selectedBusiness?.id || ''}
        onCategoriesChange={fetchCategories}
      />
    </div>
  );
}
